import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Pagination,
  Tooltip,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Avatar,
  Stack,
  Divider
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Pets as PetsIcon,
  Male as MaleIcon,
  Female as FemaleIcon,
  Close as CloseIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  MedicalServices as MedicalIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';
import { animalService, Animal, CreateAnimalData } from '../../services/animalService';
import proprietaireService, { ProprietaireResponse } from '../../services/proprietaireService';

const GestionAnimaux: React.FC = () => {
  const [animaux, setAnimaux] = useState<Animal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  
  // États pour les dialogs
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedAnimal, setSelectedAnimal] = useState<Animal | null>(null);
  const [proprietaires, setProprietaires] = useState<ProprietaireResponse[]>([]);
  const [selectedProprietaire, setSelectedProprietaire] = useState<ProprietaireResponse | null>(null);

  // État du formulaire d'édition
  const [formData, setFormData] = useState<CreateAnimalData>({
    nom: '',
    date_naissance: '',
    espece: '',
    race: '',
    poids: undefined,
    sexe: 'M',
    sterilise: false,
    numero_puce: '',
    numero_tatouage: '',
    photo_url: ''
  });

  // Charger les animaux
  const loadAnimaux = async (currentPage: number = 1, search: string = '') => {
    try {
      setLoading(true);
      const response = await animalService.getAll({
        page: currentPage,
        page_size: 20,
        search: search
      });
      setAnimaux(response.results);
      setTotalCount(response.count);
      setTotalPages(Math.ceil(response.count / 20));
      setError(null);
    } catch (err) {
      setError('Erreur lors du chargement des animaux');
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  // Charger les propriétaires pour le formulaire
  const loadProprietaires = async () => {
    try {
      const response = await proprietaireService.getProprietaires(1, '');
      setProprietaires(response.results);
    } catch (err) {
      console.error('Erreur lors du chargement des propriétaires:', err);
    }
  };

  useEffect(() => {
    loadAnimaux(page, searchTerm);
    loadProprietaires();
  }, [page]);

  // Recherche avec délai
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm !== undefined) {
        setPage(1);
        loadAnimaux(1, searchTerm);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Gérer le formulaire d'édition
  const handleFormChange = (field: keyof CreateAnimalData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Ouvrir le dialog d'édition
  const handleEditAnimal = (animal: Animal) => {
    setSelectedAnimal(animal);
    setFormData({
      nom: animal.nom,
      date_naissance: animal.date_naissance || '',
      espece: animal.espece,
      race: animal.race || '',
      poids: animal.poids,
      sexe: animal.sexe,
      sterilise: animal.sterilise,
      numero_puce: animal.numero_puce || '',
      numero_tatouage: animal.numero_tatouage || '',
      photo_url: animal.photo_url || ''
    });
    setShowEditDialog(true);
  };

  // Ouvrir le dialog de détails
  const handleViewDetails = async (animal: Animal) => {
    setSelectedAnimal(animal);
    if (animal.proprietaire_details) {
      try {
        const proprietaire = await proprietaireService.getProprietaireById(animal.proprietaire);
        setSelectedProprietaire(proprietaire);
      } catch (err) {
        console.error('Erreur lors du chargement du propriétaire:', err);
      }
    }
    setShowDetailDialog(true);
  };

  // Ouvrir les détails du propriétaire
  const handleViewProprietaireDetails = async (proprietaireId: number) => {
    try {
      const proprietaire = await proprietaireService.getProprietaireById(proprietaireId);
      setSelectedProprietaire(proprietaire);
    } catch (err) {
      console.error('Erreur lors du chargement du propriétaire:', err);
    }
  };

  // Sauvegarder les modifications
  const handleSaveAnimal = async () => {
    if (!selectedAnimal) return;

    try {
      await animalService.update(selectedAnimal.id!, formData);
      setShowEditDialog(false);
      setSelectedAnimal(null);
      loadAnimaux(page, searchTerm);
    } catch (err) {
      setError('Erreur lors de la sauvegarde');
      console.error('Erreur:', err);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const getAge = (dateNaissance?: string) => {
    if (!dateNaissance) return '-';
    const today = new Date();
    const birthDate = new Date(dateNaissance);
    const age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      return `${age - 1} ans`;
    }
    return `${age} ans`;
  };

  if (loading && animaux.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Box>
              <Typography variant="h5" component="h1">
                🐾 Animaux de mes clients
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Liste des animaux des propriétaires qui vous ont choisi comme vétérinaire
              </Typography>
            </Box>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Barre de recherche */}
          <Box mb={3}>
            <TextField
              fullWidth
              placeholder="Rechercher par nom d'animal ou nom du propriétaire..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Statistiques */}
          <Box mb={2}>
            <Typography variant="body2" color="textSecondary">
              {totalCount} animal(aux) au total
            </Typography>
          </Box>

          {/* Tableau des animaux */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Animal</TableCell>
                  <TableCell>Propriétaire</TableCell>
                  <TableCell>Espèce / Race</TableCell>
                  <TableCell>Âge</TableCell>
                  <TableCell>Sexe</TableCell>
                  <TableCell>Poids</TableCell>
                  <TableCell>Identifiants</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {animaux.map((animal) => (
                  <TableRow key={animal.id}>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <Avatar 
                          src={animal.photo_url || undefined} 
                          sx={{ mr: 2, bgcolor: 'primary.main' }}
                        >
                          {!animal.photo_url && <PetsIcon />}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {animal.nom}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            Créé le {formatDate(animal.date_creation!)}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    
                    <TableCell>
                      <Box>
                        <Button
                          variant="text"
                          onClick={() => handleViewProprietaireDetails(animal.proprietaire)}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 'bold',
                            color: 'primary.main',
                            textAlign: 'left',
                            justifyContent: 'flex-start',
                            p: 0,
                            minWidth: 'auto',
                            '&:hover': {
                              backgroundColor: 'transparent',
                              textDecoration: 'underline',
                            }
                          }}
                        >
                          {animal.proprietaire_details?.prenom} {animal.proprietaire_details?.nom}
                        </Button>
                        <Typography variant="caption" color="textSecondary" display="block">
                          {animal.proprietaire_details?.email}
                        </Typography>
                      </Box>
                    </TableCell>
                    
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {animal.espece}
                        </Typography>
                        {animal.race && (
                          <Typography variant="caption" color="textSecondary">
                            {animal.race}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    
                    <TableCell>{getAge(animal.date_naissance)}</TableCell>
                    
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        {animal.sexe === 'M' ? (
                          <MaleIcon color="primary" sx={{ mr: 1 }} />
                        ) : (
                          <FemaleIcon color="secondary" sx={{ mr: 1 }} />
                        )}
                        <Box>
                          <Typography variant="body2">
                            {animal.sexe === 'M' ? 'Mâle' : 'Femelle'}
                          </Typography>
                          {animal.sterilise && (
                            <Chip label="Stérilisé" size="small" color="info" />
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    
                    <TableCell>
                      {animal.poids ? `${animal.poids} kg` : '-'}
                    </TableCell>
                    
                    <TableCell>
                      <Box>
                        {animal.numero_puce && (
                          <Typography variant="caption" display="block">
                            Puce: {animal.numero_puce}
                          </Typography>
                        )}
                        {animal.numero_tatouage && (
                          <Typography variant="caption" display="block">
                            Tatouage: {animal.numero_tatouage}
                          </Typography>
                        )}
                        {!animal.numero_puce && !animal.numero_tatouage && '-'}
                      </Box>
                    </TableCell>
                    
                    <TableCell align="center">
                      <Box display="flex" gap={1} justifyContent="center">
                        <Tooltip title="Voir les détails complets">
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => handleViewDetails(animal)}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="Modifier la fiche">
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => handleEditAnimal(animal)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={3}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, newPage) => setPage(newPage)}
                color="primary"
              />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Dialog d'édition d'animal */}
      <Dialog 
        open={showEditDialog} 
        onClose={() => setShowEditDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            🐾 Modifier la fiche de {selectedAnimal?.nom}
            <IconButton onClick={() => setShowEditDialog(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Nom de l'animal"
                value={formData.nom}
                onChange={(e) => handleFormChange('nom', e.target.value)}
                required
                fullWidth
              />
              <TextField
                label="Date de naissance"
                type="date"
                value={formData.date_naissance}
                onChange={(e) => handleFormChange('date_naissance', e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Box>
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Espèce"
                value={formData.espece}
                onChange={(e) => handleFormChange('espece', e.target.value)}
                required
                fullWidth
              />
              <TextField
                label="Race"
                value={formData.race}
                onChange={(e) => handleFormChange('race', e.target.value)}
                fullWidth
              />
            </Box>
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Sexe</InputLabel>
                <Select
                  value={formData.sexe}
                  onChange={(e) => handleFormChange('sexe', e.target.value)}
                  label="Sexe"
                >
                  <MenuItem value="M">Mâle</MenuItem>
                  <MenuItem value="F">Femelle</MenuItem>
                </Select>
              </FormControl>
              
              <TextField
                label="Poids (kg)"
                type="number"
                value={formData.poids || ''}
                onChange={(e) => handleFormChange('poids', parseFloat(e.target.value) || undefined)}
                inputProps={{ step: 0.1, min: 0 }}
                fullWidth
              />
            </Box>
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Numéro de puce"
                value={formData.numero_puce}
                onChange={(e) => handleFormChange('numero_puce', e.target.value)}
                fullWidth
              />
              <TextField
                label="Numéro de tatouage"
                value={formData.numero_tatouage}
                onChange={(e) => handleFormChange('numero_tatouage', e.target.value)}
                fullWidth
              />
            </Box>
            
            <FormControl fullWidth>
              <InputLabel>Stérilisé</InputLabel>
              <Select
                value={formData.sterilise ? 'true' : 'false'}
                onChange={(e) => handleFormChange('sterilise', e.target.value === 'true')}
                label="Stérilisé"
              >
                <MenuItem value="false">Non</MenuItem>
                <MenuItem value="true">Oui</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              label="URL photo"
              value={formData.photo_url}
              onChange={(e) => handleFormChange('photo_url', e.target.value)}
              fullWidth
              helperText="URL d'une image en ligne"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowEditDialog(false)}>
            Annuler
          </Button>
          <Button 
            onClick={handleSaveAnimal}
            variant="contained"
            disabled={!formData.nom || !formData.espece}
          >
            Sauvegarder
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de détails complets */}
      <Dialog 
        open={showDetailDialog} 
        onClose={() => setShowDetailDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar 
                src={selectedAnimal?.photo_url || undefined} 
                sx={{ bgcolor: 'primary.main' }}
              >
                {!selectedAnimal?.photo_url && <PetsIcon />}
              </Avatar>
              <Box>
                <Typography variant="h6">
                  {selectedAnimal?.nom}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {selectedAnimal?.espece} {selectedAnimal?.race && `- ${selectedAnimal.race}`}
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={() => setShowDetailDialog(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedAnimal && (
            <Box>
              {/* Informations de l'animal */}
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                🐾 Informations de l'animal
              </Typography>
              <Stack spacing={2} sx={{ mb: 3 }}>
                <Box display="flex" gap={3}>
                  <Box flex={1}>
                    <Typography variant="body2" color="textSecondary">Nom</Typography>
                    <Typography variant="body1" fontWeight="medium">{selectedAnimal.nom}</Typography>
                  </Box>
                  <Box flex={1}>
                    <Typography variant="body2" color="textSecondary">Date de naissance</Typography>
                    <Typography variant="body1">
                      {selectedAnimal.date_naissance ? formatDate(selectedAnimal.date_naissance) : 'Non renseignée'}
                    </Typography>
                  </Box>
                </Box>
                
                <Box display="flex" gap={3}>
                  <Box flex={1}>
                    <Typography variant="body2" color="textSecondary">Espèce</Typography>
                    <Typography variant="body1" fontWeight="medium">{selectedAnimal.espece}</Typography>
                  </Box>
                  <Box flex={1}>
                    <Typography variant="body2" color="textSecondary">Race</Typography>
                    <Typography variant="body1">{selectedAnimal.race || 'Non renseignée'}</Typography>
                  </Box>
                </Box>
                
                <Box display="flex" gap={3}>
                  <Box flex={1}>
                    <Typography variant="body2" color="textSecondary">Sexe</Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                      {selectedAnimal.sexe === 'M' ? (
                        <MaleIcon color="primary" />
                      ) : (
                        <FemaleIcon color="secondary" />
                      )}
                      <Typography variant="body1">
                        {selectedAnimal.sexe === 'M' ? 'Mâle' : 'Femelle'}
                      </Typography>
                      {selectedAnimal.sterilise && (
                        <Chip label="Stérilisé" size="small" color="info" />
                      )}
                    </Box>
                  </Box>
                  <Box flex={1}>
                    <Typography variant="body2" color="textSecondary">Poids</Typography>
                    <Typography variant="body1">
                      {selectedAnimal.poids ? `${selectedAnimal.poids} kg` : 'Non renseigné'}
                    </Typography>
                  </Box>
                </Box>
                
                {(selectedAnimal.numero_puce || selectedAnimal.numero_tatouage) && (
                  <Box>
                    <Typography variant="body2" color="textSecondary" gutterBottom>Identifiants</Typography>
                    <Stack direction="row" spacing={2}>
                      {selectedAnimal.numero_puce && (
                        <Chip label={`Puce: ${selectedAnimal.numero_puce}`} size="small" />
                      )}
                      {selectedAnimal.numero_tatouage && (
                        <Chip label={`Tatouage: ${selectedAnimal.numero_tatouage}`} size="small" />
                      )}
                    </Stack>
                  </Box>
                )}
              </Stack>

              <Divider sx={{ my: 2 }} />

              {/* Informations du propriétaire */}
              {selectedProprietaire && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    👤 Informations du propriétaire
                  </Typography>
                  <Stack spacing={2}>
                    <Box display="flex" gap={3}>
                      <Box flex={1}>
                        <Typography variant="body2" color="textSecondary">Nom complet</Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {selectedProprietaire.prenom} {selectedProprietaire.nom}
                        </Typography>
                      </Box>
                      <Box flex={1}>
                        <Typography variant="body2" color="textSecondary">Téléphone</Typography>
                        <Box display="flex" alignItems="center" gap={1}>
                          <PhoneIcon fontSize="small" color="action" />
                          <Typography variant="body1">{selectedProprietaire.telephone}</Typography>
                        </Box>
                      </Box>
                    </Box>
                    
                    <Box>
                      <Typography variant="body2" color="textSecondary">Email</Typography>
                      <Box display="flex" alignItems="center" gap={1}>
                        <EmailIcon fontSize="small" color="action" />
                        <Typography variant="body1">{selectedProprietaire.email}</Typography>
                      </Box>
                    </Box>
                    
                    <Box>
                      <Typography variant="body2" color="textSecondary">Adresse</Typography>
                      <Box display="flex" alignItems="flex-start" gap={1}>
                        <LocationIcon fontSize="small" color="action" sx={{ mt: 0.5 }} />
                        <Typography variant="body1">
                          {selectedProprietaire.adresse || 'Non renseignée'}
                          {selectedProprietaire.code_postal && `, ${selectedProprietaire.code_postal}`}
                          {selectedProprietaire.ville && ` ${selectedProprietaire.ville}`}
                        </Typography>
                      </Box>
                    </Box>
                  </Stack>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Actions rapides */}
              <Typography variant="h6" gutterBottom>
                ⚡ Actions rapides
              </Typography>
              <Stack direction="row" spacing={2}>
                <Button
                  variant="outlined"
                  startIcon={<MedicalIcon />}
                  onClick={() => {
                    setShowDetailDialog(false);
                    // TODO: Ouvrir la création de consultation
                  }}
                >
                  Nouvelle consultation
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<CalendarIcon />}
                  onClick={() => {
                    setShowDetailDialog(false);
                    // TODO: Ouvrir la création de RDV
                  }}
                >
                  Prendre RDV
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => {
                    setShowDetailDialog(false);
                    handleEditAnimal(selectedAnimal);
                  }}
                >
                  Modifier la fiche
                </Button>
              </Stack>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDetailDialog(false)}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GestionAnimaux;
